var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { Alert, AlertIcon, Button, Divider, HStack, Input, Text, VStack, useNumberInput } from '@chakra-ui/react';
import { useTranslation } from 'react-i18next';
import { InputControl, ModalBase } from '@/components';
import { useToast } from '@/hooks';
import { TestingService } from '@/services';
import { useEffect, useState } from 'react';
import { useTools } from '../context/ToolsContext';
const maxShiftDay = 5000;
const minShiftDay = 1;
export const ModalShift = ({ isOpen, onClose }) => {
    var _a, _b, _c, _d, _e;
    const { t } = useTranslation();
    const tools = useTools();
    const { showToast } = useToast();
    const [shiftDay, setShiftDay] = useState(1);
    useEffect(() => {
        var _a, _b;
        setShiftDay((_b = (_a = tools.status) === null || _a === void 0 ? void 0 : _a.study_day.current_study_day) !== null && _b !== void 0 ? _b : 1);
    }, [tools.status]);
    const { getDecrementButtonProps, getIncrementButtonProps, getInputProps } = useNumberInput({
        max: maxShiftDay,
        min: minShiftDay,
        step: 1,
        value: shiftDay
    });
    const input = getInputProps();
    input.onInput = (e) => {
        if (e.target.value === '') {
            setShiftDay('');
        }
        const numberInput = Number.parseInt(e.target.value);
        if (!Number.isNaN(numberInput)) {
            // Clamp number between max and min shift day
            setShiftDay(Math.min(Math.max(numberInput, minShiftDay), maxShiftDay));
        }
    };
    const increment = getIncrementButtonProps();
    increment.onClick = () => {
        setShiftDay((prev) => (Number.isNaN(prev) ? 0 : prev) + 1);
    };
    const decrement = getDecrementButtonProps();
    decrement.onClick = () => {
        setShiftDay((prev) => {
            const num = Number.isNaN(prev) ? 0 : prev;
            return num > 1 ? num - 1 : num;
        });
    };
    function goToNextDayWithEvent() {
        if (tools.status.study_day.current_study_day >= tools.status.study_day.final_event_in_phase) {
            showToast({
                message: 'You have already completed all events in the current phase.',
                status: 'info'
            });
            return;
        }
        setShiftDay(tools.status.study_day.next_event_in_phase);
    }
    function goToFinalDayWithEvent() {
        setShiftDay(tools.status.study_day.final_event_in_phase);
    }
    const handleSubmit = () => __awaiter(void 0, void 0, void 0, function* () {
        if (Number.isNaN(shiftDay)) {
            return;
        }
        tools.setIsLoading(true);
        const success = yield TestingService.putTimeWarpShift(shiftDay);
        if (success) {
            const previousDay = tools.status.study_day.current_study_day;
            // Trigger a refresh in the background
            tools.fetchStatus().then(() => tools.setIsLoading(false));
            showToast({
                message: `Shifted from study day ${previousDay}`,
                status: 'success'
            });
            onClose();
        }
        else {
            tools.setIsLoading(false);
            showToast({
                message: 'There was an error applying the time warp. Please try again',
                status: 'error'
            });
        }
    });
    return (_jsx(ModalBase, Object.assign({ icon: 'calendar-shift', isOpen: isOpen, onClose: onClose, size: 'xl', subtitle: t('page:tool.time-warp.shift.modal-subtitle'), title: t('page:tool.time-warp.shift.title') }, { children: _jsxs(VStack, Object.assign({ spacing: '6' }, { children: [_jsxs(Alert, Object.assign({ status: 'info', textAlign: 'left' }, { children: [_jsx(AlertIcon, {}), _jsx(Text, { children: t('page:tool.time-warp.shift.modal-note') })] })), _jsx(Divider, {}), _jsxs(Text, Object.assign({ pl: '2', textAlign: 'left', textColor: 'darkBrandBlue.90', w: 'full' }, { children: [_jsxs("strong", { children: [t('page:tool.time-warp.shift.day-input'), ":\u00A0"] }), (_b = (_a = tools.status) === null || _a === void 0 ? void 0 : _a.study_day.current_study_day) !== null && _b !== void 0 ? _b : ''] })), _jsxs(InputControl, Object.assign({ label: '**Change Study Day to:**' }, { children: [_jsxs(HStack, Object.assign({ mb: 4, w: '100%' }, { children: [_jsx(Input, Object.assign({}, input, { w: '70%' })), _jsx(Button, Object.assign({}, decrement, { w: '15%' }, { children: "-" })), _jsx(Button, Object.assign({}, increment, { w: '15%' }, { children: "+" }))] })), shiftDay === maxShiftDay && (_jsx("small", { children: "The maximum number of days you can shift is 5000" }))] })), ((_c = tools.status) === null || _c === void 0 ? void 0 : _c.study_day.next_event_in_phase) && (_jsx(Button, Object.assign({ bgColor: 'darkBrandBlue.80', onClick: goToNextDayWithEvent, size: 'lg', w: '100%' }, { children: t('page:tool.time-warp.shift.next-event-day') }))), _jsx(Button, Object.assign({ bgColor: 'darkBrandBlue.70', onClick: goToFinalDayWithEvent, size: 'lg', w: '100%' }, { children: t('page:tool.time-warp.shift.final-event-day') })), _jsx(Button, Object.assign({ disabled: shiftDay === ((_e = (_d = tools.status) === null || _d === void 0 ? void 0 : _d.study_day.current_study_day) !== null && _e !== void 0 ? _e : -1), onClick: handleSubmit, size: 'lg', variant: 'success', w: '100%' }, { children: "Go!" }))] })) })));
};
